import { Outlet } from "react-router-dom"
import { usePermissions } from "@/hooks/use-permissions"
import { ForbiddenPage } from "@/pages/ForbiddenPage"
import type { PermissionModule, PermissionOperation } from "@/lib/permissions"

/**
 * Layout-route guard : renders the nested routes only if the current user holds
 * `module.operation` (default `read`), otherwise a clean "Accès non autorisé"
 * page. Defense-in-depth behind the sidebar (which already hides forbidden
 * modules) so a direct deep-link to a module the role lacks never mounts the
 * page nor fires the API calls that would 403. Admin roles bypass via
 * `usePermissions`.
 *
 * `operation` gates create-only pages (blank forms with no read use-case, e.g.
 * `quotes/new`) on `write`, so a read-only role is redirected to ForbiddenPage
 * instead of landing on a form it can neither submit nor use (its inline
 * creators would silent-fail on 403).
 *
 * Usage in the router:
 *   <Route element={<PermissionRoute module="commercial" />}>
 *     <Route path="commercial/quotes" element={<QuotesPage />} />
 *     <Route element={<PermissionRoute module="commercial" operation="write" />}>
 *       <Route path="commercial/quotes/new" element={<QuoteNewPage />} />
 *     </Route>
 *   </Route>
 */
export function PermissionRoute({
  module,
  operation = "read",
}: {
  module: PermissionModule
  operation?: PermissionOperation
}) {
  const { can, canRead } = usePermissions()

  const allowed = operation === "read" ? canRead(module) : can(module, operation)
  if (!allowed) {
    return <ForbiddenPage module={module} operation={operation} />
  }

  return <Outlet />
}
